import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, Loader2, RefreshCw, ShieldCheck, UtensilsCrossed, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/use-toast'
import { MenuBasketBar } from '@/components/menu/menu-basket-bar'
import { MenuBasketPanel } from '@/components/menu/menu-basket-panel'
import { WeekMenuCard } from '@/components/menu/week-menu-card'
import { aiRecipeParser } from '@/services/ai-recipe-parser'
import { formatWeekLabel, getCurrentWeekKey, shouldRegenerateMenu } from '@/services/menu-service'
import { useAuthStore } from '@/stores/auth-store'
import { useHouseholdStore } from '@/stores/household-store'
import { useMenuStore } from '@/stores/menu-store'
import { useRecipeStore } from '@/stores/recipe-store'
import { useSettingsStore } from '@/stores/settings-store'
import type { Recipe } from '@/types/recipe'
import type { BasketItem, WeekMenuIdeaItem, WeekMenuItem } from '@/types/menu'

const toBasketSnapshot = (recipe: Partial<Recipe>): BasketItem['recipe'] => ({
  title: recipe.title || '',
  ingredients: recipe.ingredients || [],
  servings: recipe.servings,
  image_url: recipe.image_url,
  prep_time: recipe.prep_time,
  cook_time: recipe.cook_time,
})

export default function WeekMenuPage() {
  const { user } = useAuthStore()
  const { currentHousehold, viewMode } = useHouseholdStore()
  const { aiEnabled } = useSettingsStore()
  const { recipes, loading: recipesLoading, loadRecipes, createRecipe } = useRecipeStore()
  const {
    weekKey,
    contextKey: storedContextKey,
    preferences: storedPreferences,
    items,
    usedAI,
    awaitingAI,
    isGenerating,
    error,
    basket,
    generateMenu,
    setIdeaRecipe,
    markIdeaSaved,
    addToBasket,
    removeFromBasket,
    setBasketServings,
    clearBasket,
    clearError,
  } = useMenuStore()

  const [preferencesInput, setPreferencesInput] = useState(storedPreferences)
  const [platformAIConfigured, setPlatformAIConfigured] = useState<boolean | null>(
    aiRecipeParser.peekStatus()
  )
  const [expandingKey, setExpandingKey] = useState<string | null>(null)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [basketOpen, setBasketOpen] = useState(false)
  const { toast } = useToast()

  const contextKey =
    viewMode === 'household' && currentHousehold
      ? `hh-${currentHousehold.id}`
      : `user-${user?.id ?? 'anon'}`
  const useAI = aiEnabled && platformAIConfigured !== false

  useEffect(() => {
    void aiRecipeParser.getStatus().then(setPlatformAIConfigured)
  }, [])

  useEffect(() => {
    void loadRecipes()
  }, [loadRecipes])

  useEffect(() => {
    if (recipesLoading || isGenerating || !recipes.length || platformAIConfigured === null) {
      return
    }

    const regenerate = shouldRegenerateMenu({
      weekKey,
      storedContextKey,
      currentWeekKey: getCurrentWeekKey(),
      contextKey,
      useAI,
      awaitingAI,
      hasItems: items.length > 0,
      hasError: Boolean(error),
    })

    if (regenerate) {
      void generateMenu(recipes, {
        contextKey,
        preferences: storedPreferences,
        useAI,
        userId: user?.id,
        householdId: viewMode === 'household' ? currentHousehold?.id : undefined,
      })
    }
  }, [
    recipes,
    recipesLoading,
    isGenerating,
    weekKey,
    storedContextKey,
    contextKey,
    storedPreferences,
    useAI,
    awaitingAI,
    items.length,
    error,
    platformAIConfigured,
    generateMenu,
    user?.id,
    viewMode,
    currentHousehold?.id,
  ])

  const recipesById = useMemo(() => new Map(recipes.map((recipe) => [recipe.id, recipe])), [recipes])
  const publicRecipeIds = useMemo(
    () => new Set(recipes.filter((recipe) => recipe.is_public).map((recipe) => recipe.id)),
    [recipes]
  )
  const basketKeys = useMemo(() => new Set(basket.map((item) => item.key)), [basket])

  const resolvedItems = useMemo(
    () =>
      items.filter((item) => item.kind === 'idea' || recipesById.has(item.recipeId)),
    [items, recipesById]
  )

  const handleGenerate = () => {
    void generateMenu(recipes, {
      contextKey,
      preferences: preferencesInput.trim(),
      useAI,
      userId: user?.id,
      householdId: viewMode === 'household' ? currentHousehold?.id : undefined,
    })
  }

  const expandIdea = async (item: WeekMenuIdeaItem): Promise<Partial<Recipe> | null> => {
    setExpandingKey(item.key)
    const result = await aiRecipeParser.expandRecipeIdea(
      {
        title: item.idea.title,
        description: item.idea.description,
        why_it_fits: item.idea.why_it_fits,
        prep_time: item.idea.prep_time,
        cook_time: item.idea.cook_time,
        servings: item.idea.servings,
        difficulty: item.idea.difficulty,
        meal_type: item.idea.meal_type,
        tags: item.idea.tags,
      },
      storedPreferences || 'Een gevarieerd weekmenu met avondmaaltijden voor deze week'
    )
    setExpandingKey(null)

    if (!result.success || !result.recipe) {
      toast({
        title: 'Uitwerken lukte niet',
        description: result.error || 'De app kon dit voorstel niet uitwerken tot een recept.',
        variant: 'destructive',
      })
      return null
    }

    setIdeaRecipe(item.key, result.recipe)
    return result.recipe
  }

  const handleToggleBasket = async (item: WeekMenuItem) => {
    if (basketKeys.has(item.key)) {
      removeFromBasket(item.key)
      return
    }

    if (item.kind === 'collection') {
      const recipe = recipesById.get(item.recipeId)
      if (!recipe) {
        return
      }

      addToBasket({
        key: item.key,
        source: 'collection',
        recipeId: recipe.id,
        servings: recipe.servings || 4,
        recipe: toBasketSnapshot(recipe),
      })
      return
    }

    // New AI dishes only carry a title until expanded, so fill in the full
    // recipe on demand and keep the single-click add behaviour.
    const recipe = item.recipe ?? (await expandIdea(item))
    if (!recipe) {
      return
    }

    addToBasket({
      key: item.key,
      source: item.savedRecipeId ? 'collection' : 'ai',
      recipeId: item.savedRecipeId,
      servings: recipe.servings || 4,
      recipe: toBasketSnapshot(recipe),
    })
  }

  const handleSaveIdea = async (item: WeekMenuIdeaItem) => {
    if (!item.recipe?.title || !item.recipe.ingredients?.length || !item.recipe.instructions?.length) {
      return
    }

    setSavingKey(item.key)
    const created = await createRecipe(
      {
        title: item.recipe.title || '',
        description: item.recipe.description || '',
        ingredients: item.recipe.ingredients || [],
        instructions: item.recipe.instructions || [],
        prep_time: item.recipe.prep_time || null,
        cook_time: item.recipe.cook_time || null,
        servings: item.recipe.servings || null,
        difficulty: item.recipe.difficulty || '',
        meal_type: item.recipe.meal_type || 'diner',
        tags: item.recipe.tags || [],
        equipment: item.recipe.equipment || [],
        image_url: item.recipe.image_url || '',
        rating: null,
        notes: item.recipe.notes || '',
      },
      viewMode === 'household' ? currentHousehold?.id : undefined
    )
    setSavingKey(null)

    if (!created) {
      toast({
        title: 'Bewaren lukte niet',
        description: 'Het recept kon niet worden opgeslagen. Probeer het opnieuw.',
        variant: 'destructive',
      })
      return
    }

    markIdeaSaved(item.key, created.id)
    toast({
      title: 'Recept bewaard',
      description: `"${created.title}" staat nu in je collectie.`,
    })
  }

  const currentWeekLabel = formatWeekLabel(weekKey ?? getCurrentWeekKey())
  const showEmptyState = !recipesLoading && !isGenerating && resolvedItems.length === 0

  return (
    <div className="container py-8 pb-28">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium uppercase tracking-wide text-orange-600">Weekmenu</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900 dark:text-slate-50">
            {currentWeekLabel}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-slate-300">
            Kies je gerechten voor deze week — de boodschappenlijst maken wij.
          </p>
        </div>

        <Button asChild variant="outline" size="sm">
          <Link to="/weekmenu/pool">
            <ShieldCheck className="mr-2 h-4 w-4" />
            Gerechtenpool
          </Link>
        </Button>
      </div>

      <div className="mb-8 flex flex-col gap-3 sm:flex-row">
        <Input
          value={preferencesInput}
          onChange={(event) => setPreferencesInput(event.target.value)}
          placeholder="Bijv. veel groenten, één keer vis, niet te lang in de keuken..."
          className="sm:max-w-md"
          disabled={!useAI}
        />
        <Button onClick={handleGenerate} disabled={isGenerating || !recipes.length}>
          {isGenerating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Nieuw menu
        </Button>
      </div>

      {!useAI && platformAIConfigured !== null && (
        <p className="mb-6 text-sm text-gray-500 dark:text-slate-400">
          Slimme menusamenstelling staat uit — je ziet de vaste rotatie van deze week.
        </p>
      )}

      {error && (
        <div className="mb-6 flex items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
          <button onClick={clearError} aria-label="Sluit melding">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {isGenerating ? (
        <div className="flex flex-col items-center justify-center py-24 text-gray-500 dark:text-slate-300">
          <Loader2 className="mb-4 h-8 w-8 animate-spin text-orange-600" />
          <p>Weekmenu samenstellen...</p>
        </div>
      ) : showEmptyState ? (
        <Card>
          <CardContent className="py-16 text-center">
            <UtensilsCrossed className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h2 className="mb-2 text-lg font-medium text-gray-900 dark:text-slate-50">
              Nog geen recepten om een weekmenu van te maken
            </h2>
            <p className="mx-auto mb-6 max-w-md text-sm text-gray-600 dark:text-slate-300">
              Het weekmenu kiest uit echte recepten in je collectie. Voeg eerst recepten toe, of
              zet de Nederlandse klassiekers klaar via Instellingen → Data Beheer.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild>
                <Link to="/recepten/nieuw">Voeg een recept toe</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/instellingen">Naar instellingen</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {resolvedItems.map((item) => (
            <WeekMenuCard
              key={item.key}
              item={item}
              recipe={
                item.kind === 'collection'
                  ? recipesById.get(item.recipeId)
                  : item.savedRecipeId
                    ? recipesById.get(item.savedRecipeId)
                    : undefined
              }
              inBasket={basketKeys.has(item.key)}
              onToggleBasket={() => void handleToggleBasket(item)}
              isExpanding={expandingKey === item.key}
              onSaveIdea={item.kind === 'idea' ? () => void handleSaveIdea(item) : undefined}
              isSaving={savingKey === item.key}
            />
          ))}
        </div>
      )}

      {resolvedItems.length > 0 && !isGenerating && (
        <p className="mt-6 text-xs text-gray-400 dark:text-slate-500">
          {usedAI ? (
            <>
              Dit menu combineert jouw recepten met gecontroleerde gerechten uit de pool, aangevuld
              met verse voorstellen. Publiceer voorstellen naar de{' '}
              <Link to="/weekmenu/pool" className="underline">
                gerechtenpool
              </Link>{' '}
              zodat ze nagekeken en zonder AI hergebruikt worden.
            </>
          ) : (
            <>
              Dit menu komt volledig uit jouw recepten en de gecontroleerde gerechtenpool — er is
              niets gegenereerd. Volgende week staat er automatisch een nieuw menu klaar.
            </>
          )}
        </p>
      )}

      <MenuBasketBar count={basket.length} onOpen={() => setBasketOpen(true)} />
      <MenuBasketPanel
        open={basketOpen}
        onClose={() => setBasketOpen(false)}
        items={basket}
        publicRecipeIds={publicRecipeIds}
        onSetServings={setBasketServings}
        onRemove={removeFromBasket}
        onClear={clearBasket}
      />
    </div>
  )
}
